import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MuiModal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { forwardRef, memo, useEffect } from 'react';

import useScreenSize from '../../util/hooks/useScreenSize';
import IconClose from '../Icons/IconClose';

/**
 * @param {import('@mui/material/styles').Theme} theme
 * @returns {object}
 */
const modalStyles = (theme) => ({
  position: 'absolute',
  top: {
    xs: 0,
    md: '50%',
  },
  left: {
    xs: 0,
    md: '50%',
  },
  transform: {
    xs: 'translate(0%, 0%)',
    md: 'translate(-50%, -50%)',
  },
  borderRadius: {
    xs: 0,
    md: 2,
  },
  backgroundColor: theme.palette.background.paper,
  padding: {
    xs: 2,
    md: 4,
  },
  boxShadow: theme.shadows[5],
  overflowY: 'auto',
  height: {
    xs: '100%',
    md: 'auto',
  },
  maxHeight: {
    xs: '100%',
    md: '90%',
  },
  maxWidth: {
    xs: '100%',
    md: '80%',
    xl: '40%',
  },
  width: {
    xs: '100%',
    md: 'auto',
  },
});

const contentBoxStylesDefaults = {
  width: '100%',
  px: {
    xs: 0,
    md: 2,
    xl: 4,
  },
};

/**
 * @param {object} props Component props
 * @param {import('@mui/material').ModalProps | {}} [props.modalProps] Modal props
 * @param {React.ReactNode[]} [props.actions] Actions
 * @param {React.ReactNode} props.children Children
 * @param {boolean} props.open Is modal open
 * @param {string | string[] | React.ReactNode} [props.title] Modal title
 * @param {() => void} props.onClose Function to close modal
 * @param {import('@mui/material/styles').SxProps} [props.containerStyles] Container styles
 * @param {import('@mui/material/styles').SxProps} [props.contentBoxStyles] Content box styles
 * @param {React.Ref<any>} ref React ref
 * @param {boolean} [props.disableClose] - Disables the close button in the corner
 * @returns {React.ReactNode}
 */

const ForwardRefModalComponent = (
  {
    actions = [],
    children,
    title = '',
    open,
    onClose,
    modalProps = {},
    containerStyles = {},
    contentBoxStyles = {},
    disableClose = false,
  },
  ref,
) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  useEffect(() => {
    const listener = (e) => {
      if (e.key === 'Escape' && typeof onClose === 'function') {
        onClose();
      }
    };

    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [onClose]);

  return (
    <MuiModal
      disableAutoFocus
      onClose={onClose}
      open={open}
      ref={ref}
      {...modalProps}
      aria-modal={true}
      disableEnforceFocus={false}
      disablePortal={false}
      keepMounted={false}
      sx={{
        '&:focus, &:focus-visible': { outline: 'none' },
      }}
    >
      <Box sx={{ ...modalStyles(theme), ...containerStyles }}>
        <Stack
          spacing={4}
          sx={{
            height: '100%',
            outline: 'none',
            '&:focus, &:focus-visible': { outline: 'none' },
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" spacing={10}>
              <Typography component="h4" variant="h5">
                {title}
              </Typography>
              {!disableClose ? (
                <IconButton onClick={onClose}>
                  <IconClose />
                </IconButton>
              ) : null}
            </Stack>
            <Divider />
          </Stack>
          <Box sx={{ ...contentBoxStylesDefaults, ...contentBoxStyles }}>{children}</Box>
          {actions.length > 0 ? (
            <Stack direction={isMobile ? 'column' : 'row'} justifyContent="center" spacing={2}>
              {actions}
            </Stack>
          ) : null}
        </Stack>
      </Box>
    </MuiModal>
  );
};

const ModalComponent = forwardRef(ForwardRefModalComponent);

ModalComponent.displayName = 'Modal';

const Modal = memo(ModalComponent);

export default Modal;
